import i18n from "i18next";
import type { TFunction } from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { useContext, useMemo } from "preact/hooks";
import en from "./en.json";
import { navigatorLanguages, safeSessionStorage } from "../app/ssrEnv";
import { createContext, type ComponentChildren } from "preact";
import type { NavigationManager } from "../managers/NavigationManager";
import { computed, signal } from "@preact/signals";
import {
  bibleLanguageToUiLocale,
  getNearestBibleTranslationForUiLanguage,
  type TranslationWithLanguage,
} from "../managers/BibleReadingManager";
import type { Translation } from "../managers/FreeUseBibleAPI";
import {
  DEFAULT_UI_LANGUAGE,
  parseReadingPath,
} from "../managers/ReadingUrlPath";
import { parseStaticPagePath } from "../managers/StaticPagePath";
import { parseSharedPagePath } from "../managers/SharedPagePath";
import type { BrandingConfig } from "../app/appConfig";

function getLanguageName(importPath: string): string {
  const match = importPath.match(/\.\/([a-z-]+)\.json$/i);
  if (match) {
    return match[1]!;
  }
  throw new Error(`Could not extract language code from path: ${importPath}`);
}

/**
 * Lazy per-language loaders keyed by import path (e.g. "./es.json"). Using a
 * non-eager glob means each locale becomes its own Vite chunk, fetched on
 * demand rather than bundled up front. The keys are available synchronously, so
 * we can still enumerate the supported languages without loading any file.
 */
const localeLoaders = import.meta.glob("./*.json") as Record<
  string,
  () => Promise<{ default: Record<string, string> }>
>;

export { i18n };

export type BotTranslations = Record<string, Record<string, string>>;

function getLanguage(locale: string | null | undefined): string | null {
  if (!locale) {
    return null;
  }
  const normalized = locale.toLowerCase().replace(/_/g, "-");
  const [language] = normalized.split("-");
  return language || null;
}

/**
 * Adds the given translations to the i18n instance under the specified namespace.
 * @param ns The namespace for the translations, typically the extension ID to avoid conflicts with other extensions.
 * @param translations The translations to add, keyed by language code (e.g. "en", "es"), with each containing a "translation" object mapping translation keys to translated strings.
 * @param options The options for adding the translations.
 */
export function addTranslations(
  ns: string,
  translations: BotTranslations,
  options?: { overwrite?: boolean }
) {
  for (const [lang, resources] of Object.entries(translations)) {
    i18n.addResourceBundle(lang, ns, resources, true, options?.overwrite);
  }
}
type TranslationFn = (key: string, options?: Record<string, unknown>) => string;
function getAppName(
  t: TranslationFn,
  branding?: BrandingConfig,
  customizationName?: string | null
): string {
  return (
    customizationName ??
    branding?.appName ??
    t("seed-bible", { defaultValue: "Seed Bible" })
  );
}

/**
 * Replaces occurrences of "Seed Bible" in the given text with the branded app name, which can be customized via the branding configuration.
 * This function is useful for ensuring that the app's name is consistently displayed according to the user's branding preferences.
 * Currently, this only means that English is fully supported for branding.
 * @param text The text in which to replace "Seed Bible" with the branded app name.
 * @param t The translation function to use for retrieving the branded app name. This is typically obtained from the i18n manager.
 * @param branding The branding configuration that may contain a custom app name. If not provided, the default app name "Seed Bible" will be used.
 * @param customizationName The name of the active Customization (loaded via a `?customization=...` link, or previewed in its editor), if any. Takes priority over `branding.appName` — a customization's own name is a more specific override than the deployment's default branding.
 * @returns
 */
export function getBrandedAppText(
  text: string,
  t: TranslationFn,
  branding?: BrandingConfig,
  customizationName?: string | null
): string {
  const appName = getAppName(t, branding, customizationName);
  return text.replace(/Seed Bible/gi, appName);
}
// /**
//  * Loads translations from the given bot's tags.
//  * Each tag with a key of 3 characters or less is considered a language code, and its value is expected to be a JSON string or an object containing the translations for that language.
//  * @param bot The bot from which to load translations. Typically this would be the config bot or a dedicated locales bot.
//  * @returns A record of translations keyed by language code, where each value is an object containing a "translation" object mapping translation keys to translated strings.
//  */
// function getTranslations(bot: Bot): BotTranslations {
//   // os.log("Loading translations from bot tags...", localesBot);
//   const loadedResources: BotTranslations = {};
//   for (const langCode of Object.keys(bot.tags ?? {})) {
//     if (langCode.length > 3) {
//       continue; // Skip non-language tags
//     }
//     const translations = bot.tags[langCode];
//     if (translations) {
//       loadedResources[langCode] =
//         typeof translations === "string"
//           ? JSON.parse(translations)
//           : translations;
//     }
//   }

//   return loadedResources;
// }

const availableLanguages = Object.keys(localeLoaders)
  .map((path) => getLanguageName(path))
  .sort();

export function getInitialLanguage(acceptedLanguages: string[]): string {
  if (import.meta.env.SSR) {
    const ssrLang = getLanguage(acceptedLanguages[0]);
    if (ssrLang) {
      return ssrLang;
    }
  }

  return getLanguage(navigatorLanguages()[0]) ?? DEFAULT_UI_LANGUAGE;
}

/**
 * Picks the visitor's most-preferred UI language, out of an `Accept-Language`
 * header's ordered list of tags, that this app actually ships a locale for.
 * Returns null when none of the visitor's preferences match a supported
 * language — the caller should keep whatever it already had rather than
 * guess.
 */
export function getPreferredSupportedLanguage(
  acceptedLanguages: string[]
): string | null {
  for (const tag of acceptedLanguages) {
    const language = getLanguage(tag);
    if (language && availableLanguages.includes(language)) {
      return language;
    }
  }
  return null;
}

/**
 * Resolves the UI language a URL implies. A valid reading path (e.g.
 * "/es/spa_onbv/john/3") takes priority: an explicit `{lang}` segment wins,
 * and an omitted one canonically means `DEFAULT_UI_LANGUAGE` — that's the
 * meaning of the 3-segment "fully default" form, not "detect from the
 * browser" (browser-based detection only applies to a bare `/` with no
 * reading path at all, via `getInitialLanguage`). Next, a static page path
 * (e.g. "/es/about") or a shared playlist or reading plan page path always
 * names its language explicitly. Falls back to the legacy `?lang=` query
 * param for anything else.
 */
export function getUrlLanguage(url: URL, basePath: string): string | null {
  const parsed = parseReadingPath(url.pathname, basePath);
  if (parsed) {
    return parsed.language ?? DEFAULT_UI_LANGUAGE;
  }

  const staticPage = parseStaticPagePath(url.pathname, basePath);
  if (staticPage) {
    return staticPage.language;
  }

  const sharedPage = parseSharedPagePath(url.pathname, basePath);
  if (sharedPage) {
    return sharedPage.language;
  }

  const urlLang = url.searchParams.get("lang");
  if (urlLang) {
    return urlLang;
  }
  return null;
}

/**
 * Shown when the UI language has no direct Bible text and we would switch the
 * reader to a nearest available translation (e.g. Gujarati → Hindi).
 */
export type LanguageFallbackPrompt = {
  requestedLanguage: string;
  fallbackLanguage: string;
  fallbackTranslation: TranslationWithLanguage;
};

/**
 * Shown after the user deliberately picks a Bible translation written in a
 * language the UI also supports, offering to move the interface to that
 * language too.
 */
export type UiLanguageSwitchPrompt = {
  /** UI language the picked translation is written in (e.g. "es"). */
  targetLanguage: string;
  /**
   * Translator fixed to `targetLanguage`, so the prompt is written in the
   * language it is offering — someone who can't read the current UI language
   * is exactly who this prompt is for.
   */
  t: (key: string, options?: Record<string, unknown>) => string;
};

/**
 * Reads/clears the user's "never ask again" answer to the UI-language switch
 * prompt. Supplied by SeedBibleState so this manager doesn't need to know
 * whether the preference lives on the account or on the device.
 */
export type UiLanguagePromptPreference = {
  isEnabled: () => boolean;
  disable: () => void;
};

/**
 * sessionStorage key recording that the UI-language switch prompt has already
 * been shown in this tab. Deliberately session-scoped, not persisted: the
 * prompt is a suggestion, and repeating it once per session is the most it
 * should ever intrude.
 */
const UI_LANGUAGE_PROMPT_SHOWN_KEY = "sb.uiLanguagePromptShown";

export function createI18nManager(
  navigation: NavigationManager,
  acceptedLanguages: string[]
) {
  const initialLanguage = getInitialLanguage(acceptedLanguages);

  const url = navigation.currentUrl.value;

  // Computed at module load. During SSR `location`/`navigator` are absent, so
  // this falls back to "en"; the client re-derives the real language from the
  // URL/navigator at hydration.
  const defaultLanguage: string =
    getUrlLanguage(url, navigation.basePath) ?? initialLanguage;

  // Resolves once the detected language's translations are loaded. SSR and the
  // client entry await this before rendering so the first paint is in the right
  // language rather than the bundled "en" fallback.
  let ready: Promise<unknown>;

  if (!i18n.isInitialized) {
    // Fetch each (non-bundled) language's JSON chunk on demand. Only the
    // "seed-bible" namespace is file-backed; extension namespaces are supplied
    // directly via `addTranslations`/`addResourceBundle`.
    i18n.use(
      resourcesToBackend((language: string, namespace: string) => {
        if (namespace !== "seed-bible") {
          return Promise.reject(new Error(`Unknown namespace: ${namespace}`));
        }
        const loader = localeLoaders[`./${language}.json`];
        if (!loader) {
          return Promise.reject(
            new Error(`No locale file for language: ${language}`)
          );
        }
        // Return the module namespace object as-is: `resourcesToBackend`
        // already unwraps `.default` itself (`(data && data.default) || data`
        // in its `read()`). Unwrapping it here too double-unwraps any locale
        // whose JSON has a top-level key literally named "default" (e.g.
        // ar.json's `"default": "تقصير"`) — its own `.default` re-unwrap then
        // grabs that string instead of falling through to the whole object.
        return loader();
      })
    );

    ready = i18n.init({
      lng: defaultLanguage,
      fallbackLng: DEFAULT_UI_LANGUAGE,
      ns: ["seed-bible"],
      // Required so the backend is still consulted for languages beyond the
      // bundled resources below.
      partialBundledLanguages: true,
      interpolation: {
        escapeValue: false,
      },
      // English is bundled so a synchronous fallback is always present (notably
      // during SSR); every other language is fetched lazily by the backend.
      resources: { en: { "seed-bible": en } },
    });
  } else {
    ready = i18n.changeLanguage(defaultLanguage);
  }

  const language = signal(i18n.language);
  const onLanguageChanged = (lng: string) => {
    language.value = lng;
  };
  i18n.on("languageChanged", onLanguageChanged);

  /**
   * `i18n` is the process-wide i18next instance, so its listener list outlives
   * this manager. The SSR host builds a manager per request; without this, each
   * one's listener (and, through `language`, its whole app state) stays alive
   * for the life of the server process.
   */
  const dispose = () => {
    i18n.off("languageChanged", onLanguageChanged);
  };

  // URL <-> language sync (both directions) is owned by `TabsManager`, not
  // here: the language segment is part of the same coordinated reading path
  // as translation/book/chapter (e.g. "/es/spa_onbv/john/3"), so a single
  // writer needs to own the whole path instead of this manager independently
  // touching the URL. `TabsManager.syncSelectedTabFromUrl` calls
  // `changeLanguage` directly when an external URL change implies a
  // different language; the `commitSelectedTabToUrl` effect there writes the
  // language segment back out.

  const isRtl = computed(() => isRightToLeftLanguage(language.value));

  const languageFallbackPrompt = signal<LanguageFallbackPrompt | null>(null);

  /**
   * Wired by SeedBibleState so UI language changes also select the nearest
   * available Bible translation. Direct matches apply silently; fallback
   * suggestions (e.g. Gujarati → Hindi) show a warning modal first.
   */
  let applyBibleTranslation:
    | ((translation: TranslationWithLanguage) => Promise<void>)
    | null = null;
  let getAvailableTranslations: (() => readonly Translation[] | null) | null =
    null;
  let ensureTranslationsLoaded:
    | (() => Promise<readonly Translation[] | null>)
    | null = null;

  const setBibleTranslationApplicator = (
    applicator:
      | ((translation: TranslationWithLanguage) => Promise<void>)
      | null,
    getTranslations: (() => readonly Translation[] | null) | null = null,
    loadTranslations:
      | (() => Promise<readonly Translation[] | null>)
      | null = null
  ) => {
    applyBibleTranslation = applicator;
    getAvailableTranslations = getTranslations;
    ensureTranslationsLoaded = loadTranslations;
  };

  /**
   * Wired by SeedBibleState to persist the user's chosen UI language (e.g. to
   * their profile). Invoked ONLY for selector-driven changes via
   * `requestLanguageChange` — never for URL-driven changes (deep links,
   * browser back/forward) or profile-applied changes, so opening a shared
   * `?lang=` link updates the view without overwriting the account's saved
   * language.
   */
  let persistLanguage: ((language: string) => void) | null = null;
  const setLanguagePersister = (
    persister: ((language: string) => void) | null
  ) => {
    persistLanguage = persister;
  };

  // Explicitly typed (dropping the rarely-used `callback` second parameter,
  // never passed by any caller here) rather than left inferred from
  // `.bind()`: the inferred type otherwise carries i18next's internal,
  // non-portable `Callback` type, which TypeScript's declaration emitter
  // refuses to print (TS2883) — this only surfaces when actually emitting
  // `.d.ts` output (e.g. `seed-bible-extension-scripts`'s vendored-types
  // sync), never during this repo's own `noEmit` type-checking.
  const changeLanguage: (lng?: string) => Promise<TFunction> =
    i18n.changeLanguage.bind(i18n);

  const applyBibleTranslationForUiLanguage = async (uiLanguage: string) => {
    let available = getAvailableTranslations?.() ?? null;
    if (!available?.length && ensureTranslationsLoaded) {
      available = (await ensureTranslationsLoaded()) ?? null;
    }

    const nearest = getNearestBibleTranslationForUiLanguage(
      uiLanguage,
      available
    );

    // Direct support: apply silently. Nearest suggestion: ask first.
    if (nearest.usedFallback) {
      languageFallbackPrompt.value = {
        requestedLanguage: uiLanguage,
        fallbackLanguage: nearest.resolvedUiLanguage,
        fallbackTranslation: nearest.translation,
      };
      return;
    }

    languageFallbackPrompt.value = null;
    if (!applyBibleTranslation) {
      return;
    }
    await applyBibleTranslation(nearest.translation);
  };

  const requestLanguageChange = async (nextLanguage: string) => {
    if (nextLanguage !== language.value) {
      await changeLanguage(nextLanguage);
    }
    // Persist the user's explicit selection. Only selector-driven changes reach
    // this function; URL-driven changes go through the `syncSignalsToUrl`
    // setter above and are deliberately left un-persisted.
    persistLanguage?.(nextLanguage);
    await applyBibleTranslationForUiLanguage(nextLanguage);
  };

  const confirmLanguageFallback = async () => {
    const prompt = languageFallbackPrompt.value;
    if (!prompt) {
      return;
    }
    languageFallbackPrompt.value = null;
    await applyBibleTranslation?.(prompt.fallbackTranslation);
  };

  const cancelLanguageFallback = () => {
    languageFallbackPrompt.value = null;
  };

  const uiLanguageSwitchPrompt = signal<UiLanguageSwitchPrompt | null>(null);

  let uiLanguagePromptPreference: UiLanguagePromptPreference | null = null;
  const setUiLanguagePromptPreference = (
    preference: UiLanguagePromptPreference | null
  ) => {
    uiLanguagePromptPreference = preference;
  };

  /**
   * The mirror image of `applyBibleTranslationForUiLanguage`: the user picked
   * the Bible text first, so offer to move the interface to match it.
   *
   * Stays silent unless every condition holds — the translation's language maps
   * onto a UI language we actually ship, that language isn't the current one,
   * the user hasn't turned the prompt off, and it hasn't already appeared this
   * session.
   */
  const maybePromptUiLanguageSwitch = (
    bibleLanguage: string | null | undefined
  ) => {
    if (uiLanguagePromptPreference && !uiLanguagePromptPreference.isEnabled()) {
      return;
    }

    const targetLanguage = bibleLanguageToUiLocale(bibleLanguage);
    if (
      !targetLanguage ||
      targetLanguage === language.value ||
      !availableLanguages.includes(targetLanguage)
    ) {
      return;
    }

    if (safeSessionStorage.getItem(UI_LANGUAGE_PROMPT_SHOWN_KEY)) {
      return;
    }
    safeSessionStorage.setItem(UI_LANGUAGE_PROMPT_SHOWN_KEY, "true");

    uiLanguageSwitchPrompt.value = {
      targetLanguage,
      t: i18n.getFixedT(targetLanguage, "seed-bible"),
    };

    // Every language but English is fetched on demand, so the first `t` above
    // may still be falling back to English. Re-fix it once the chunk lands and
    // let the modal re-render into the real text.
    void i18n.loadLanguages(targetLanguage).then(() => {
      const current = uiLanguageSwitchPrompt.peek();
      if (current?.targetLanguage !== targetLanguage) {
        return;
      }
      uiLanguageSwitchPrompt.value = {
        ...current,
        t: i18n.getFixedT(targetLanguage, "seed-bible"),
      };
    });
  };

  const confirmUiLanguageSwitch = async () => {
    const prompt = uiLanguageSwitchPrompt.value;
    if (!prompt) {
      return;
    }
    uiLanguageSwitchPrompt.value = null;

    // `changeLanguage` + `persistLanguage`, not `requestLanguageChange`: the
    // latter also swaps the Bible text to the new UI language's default
    // translation, which would undo the very pick that raised this prompt.
    await changeLanguage(prompt.targetLanguage);
    persistLanguage?.(prompt.targetLanguage);
  };

  const dismissUiLanguageSwitch = () => {
    uiLanguageSwitchPrompt.value = null;
  };

  const neverAskUiLanguageSwitch = () => {
    uiLanguageSwitchPrompt.value = null;
    uiLanguagePromptPreference?.disable();
  };

  return {
    i18n,
    t: i18n.t.bind(i18n),
    changeLanguage,
    requestLanguageChange,
    confirmLanguageFallback,
    cancelLanguageFallback,
    setBibleTranslationApplicator,
    setLanguagePersister,
    languageFallbackPrompt,
    uiLanguageSwitchPrompt,
    setUiLanguagePromptPreference,
    maybePromptUiLanguageSwitch,
    confirmUiLanguageSwitch,
    dismissUiLanguageSwitch,
    neverAskUiLanguageSwitch,
    defaultLanguage,
    availableLanguages,
    language,
    isRtl,
    ready,
    dispose,
  };
}

export type I18nManager = ReturnType<typeof createI18nManager>;
const I18nContext = createContext(null as I18nManager | null);

export function I18nProvider(props: {
  i18n: I18nManager;
  children: ComponentChildren;
}) {
  return (
    <I18nContext.Provider value={props.i18n}>
      {props.children}
    </I18nContext.Provider>
  );
}

export type I18nHook = ReturnType<typeof useI18n>;

const RTL_LANGUAGE_CODES = new Set(["ar", "fa", "he", "ur", "ps", "dv", "yi"]);

export function isRightToLeftLanguage(languageCode: string): boolean {
  const normalizedCode = languageCode.trim();
  if (!normalizedCode) {
    return false;
  }

  const primarySubtag = normalizedCode.split("-")[0]?.toLowerCase();
  if (primarySubtag && RTL_LANGUAGE_CODES.has(primarySubtag)) {
    return true;
  }

  if (typeof Intl !== "undefined" && typeof Intl.Locale === "function") {
    try {
      const locale = new Intl.Locale(normalizedCode) as Intl.Locale & {
        textInfo?: { direction: string };
        getTextInfo?: () => { direction: string };
      };
      if (typeof locale.getTextInfo === "function") {
        const textInfo = locale.getTextInfo();
        return textInfo.direction === "rtl";
      }
      return locale.textInfo?.direction === "rtl";
    } catch {
      return false;
    }
  }

  return false;
}

/**
 * Gets the i18n manager, which provides access to the translation function, current language, available languages, and a function to change the language. Also provides a helper function for translating keys within a specific namespace.
 * @param ns The namespace for the translations, typically the extension ID to avoid conflicts with other extensions. This is optional, as the returned manager will still work without it, but it can be used to create a namespaced translation function that automatically applies the namespace to translation keys.
 * @returns
 */
export function useI18n(ns?: string) {
  const i18nManager = useContext(I18nContext);
  if (!i18nManager) {
    throw new Error("useI18n() must be used within an I18nProvider");
  }
  const i18n = i18nManager.i18n;
  const { t } = i18n;

  const isRtl = isRightToLeftLanguage(i18nManager.language.value);

  const setLanguage = async (language: string) => {
    await i18nManager.requestLanguageChange(language);
  };

  const translate = ns
    ? (key: string, options?: Record<string, unknown>) =>
        t(key, { ...options, ns })
    : t;

  return useMemo(
    () => ({
      t: translate,
      ns,
      language: i18nManager.language.value,
      isRtl,
      availableLanguages,
      setLanguage,
      requestLanguageChange: i18nManager.requestLanguageChange,
      confirmLanguageFallback: i18nManager.confirmLanguageFallback,
      cancelLanguageFallback: i18nManager.cancelLanguageFallback,
      languageFallbackPrompt: i18nManager.languageFallbackPrompt,
      uiLanguageSwitchPrompt: i18nManager.uiLanguageSwitchPrompt,
      confirmUiLanguageSwitch: i18nManager.confirmUiLanguageSwitch,
      dismissUiLanguageSwitch: i18nManager.dismissUiLanguageSwitch,
      neverAskUiLanguageSwitch: i18nManager.neverAskUiLanguageSwitch,
      i18n: i18n,
    }),
    [t, i18n.language]
  );
}
